/**
 * Firebase initialisation.
 *
 * This folder is the ONLY place in ZIvapp that may import from 'firebase/*'.
 * Everything else goes through ../index.js.
 *
 * Initialisation is lazy and happens once, on the first service call, not at
 * import time. Two reasons:
 *
 *   1. ../index.js imports all three providers eagerly so the switch can be
 *      synchronous. Initialising here at import time would mean getFirestore()
 *      runs even in mock mode, and it throws when no projectId is configured.
 *   2. The config arrives from the host app via configureZivapp(), which runs
 *      after this module is evaluated.
 *
 * `db`, `firebaseAuth` and `storage` are exported as ES live bindings: they
 * read null until ensureFirebase() runs, and every importer sees the real
 * instance afterwards without re-importing. ../index.js reads them inside
 * function bodies, never at module scope, so by the time any of them is
 * dereferenced the binding is populated.
 */

import { initializeApp, getApps } from 'firebase/app'
import { getAuth } from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'

import { getConfig, isFirebaseUsable } from '../../config.js'

let _app = null
let _firebaseAuth = null
let _db = null
let _storage = null

export { _app as app, _firebaseAuth as firebaseAuth, _db as db, _storage as storage }

/**
 * Initialise Firebase from the host app's configuration. Safe to call repeatedly:
 * the first call does the work and the rest return immediately.
 *
 * @returns {boolean} whether Firebase is usable
 */
export function ensureFirebase () {
  if (_db) return true

  const { firebase: cfg } = getConfig()
  if (!isFirebaseUsable(cfg)) return false

  // getApps() guards against a double initialise under Vite's HMR, which can
  // re-evaluate this module while the previous instance is still live.
  _app = getApps().length ? getApps()[0] : initializeApp(cfg)
  _firebaseAuth = getAuth(_app)
  _db = getFirestore(_app)
  _storage = getStorage(_app)

  return true
}

/** True once Firebase has been initialised. */
export function isConfigured () {
  return Boolean(_db)
}

/** Collection names, in one place so a rename cannot drift. */
export const COLLECTIONS = {
  users:    'users',
  chats:    'chats',
  messages: 'messages',
  receipts: 'receipts',
  status:   'status',
  views:    'views',
  calls:    'calls',
  signals:  'signals',
  contacts: 'contacts',
  devices:  'devices',
  blocked:  'blocked'
}
